import { useCallback, useEffect, useState } from 'react';

export const useScrollableChatElement = () => {
  const [chatElement, setChatElement] = useState<Element | null>(null);

  const scrollChatBy = useCallback(
    (scrollBy: number, behavior: 'auto' | 'smooth' | 'instant' = 'instant') => {
      chatElement?.scrollBy({
        top: scrollBy, // Vertical scrolling
        behavior,
      });
    },
    [chatElement],
  );

  const scrollChatToTop = useCallback(() => {
    chatElement && chatElement.scrollBy(0, -chatElement.scrollHeight);
  }, [chatElement]);

  const scrollChatToBottom = useCallback(() => {
    chatElement && chatElement.scrollBy(0, chatElement.scrollHeight);
  }, [chatElement]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const el = document.querySelector('[data-hook="scrollableChatElement"]');
      if (el) {
        setChatElement(el);
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return { chatElement, scrollChatBy, scrollChatToBottom, scrollChatToTop };
};

export const useIsChatScrolled = (offset: number = 0) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { chatElement } = useScrollableChatElement();

  useEffect(() => {
    const handleScroll = (e: Event) => {
      setIsScrolled((e.currentTarget as HTMLDivElement).scrollTop > offset);
    };

    if (chatElement) {
      chatElement.addEventListener('scroll', handleScroll);
    }

    return () => chatElement?.removeEventListener('scroll', handleScroll);
  }, [chatElement, offset]);

  return { isScrolled };
};
